import { useEffect, useState } from 'react';

import { getCurrentBreakpoint } from '@debijenkorf/app-shell';
import { i18n } from '@debijenkorf/dbk-common';
import { useProductCarouselTracking } from '@debijenkorf/product-carousel';
import { SectionHeader } from '@debijenkorf/text';

import {
  type CatalogProduct,
  ProductCarousel,
  mapCatalogProductToCarouselItem,
} from '@/components/Carousels/ProductCarousel';
import { useEnvironment } from '@/context/EnvironmentContext';
import { useLocale } from '@/context/LocaleContext';
import { useWishlist } from '@/hooks/wishlist/useWishlist';
import { getProductList } from '@/services/product-list';
import { DEFAULT_PLP_PATH } from '@/utilities/constants';

interface FullPriceProductCarouselProps {
  relatedQuery: string;
  title: string;
}

const ITEM_PREFIX = 'full-price-product-carousel';
const HEADER_ID = `${ITEM_PREFIX}-header`;

export const FullPriceProductCarousel = ({
  relatedQuery,
  title,
}: FullPriceProductCarouselProps) => {
  const [fullPriceProducts, setFullPriceProducts] = useState<CatalogProduct[]>([]);
  const { locale } = useLocale();
  const { environment } = useEnvironment();

  const analyticsDataFullPrice = {
    products: fullPriceProducts,
    analyticsData: {
      location: 'no_search_results_lister',
      listName: 'carousel_full_price',
    },
  };

  const { ref, handleAnalyticsEvent } = useProductCarouselTracking(analyticsDataFullPrice);
  const { combinedProductCodes, onWishlistChange } = useWishlist(analyticsDataFullPrice);

  const getProducts = async () => {
    const productListing = await getProductList({ resolvedUrl: relatedQuery, locale, environment });

    if (productListing) {
      setFullPriceProducts(productListing.navigation.products as any);
    }
  };

  useEffect(() => {
    getProducts();
  }, []);

  return (
    <>
      {fullPriceProducts.length > 0 ? (
        <SectionHeader
          aria-labelledby={HEADER_ID}
          title={title}
          cta={{
            title: `${i18n.t('responsive-assets.plp.search.viewAllResults')} ${title}`,
            href: `${DEFAULT_PLP_PATH}?${relatedQuery}`,
          }}
        >
          <ProductCarousel
            ref={ref}
            itemLabelledbyPrefix={ITEM_PREFIX}
            aria-labelledby={HEADER_ID}
            items={fullPriceProducts.map(mapCatalogProductToCarouselItem)}
            currentBreakpoint={getCurrentBreakpoint()}
            withInlineSpace
            onWishlistChange={onWishlistChange}
            combinedProductCodes={combinedProductCodes}
            onAnalyticsEvent={handleAnalyticsEvent}
          />
        </SectionHeader>
      ) : null}
    </>
  );
};
